import React, {
  FC,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import { useMeasure } from "@adsk/alloy-react-helpers";
import Tooltip from "@adsk/alloy-react-tooltip";

import { NoWrap } from "./commonStyledComponents";

const ellipsis = `\u2026`;

type TruncationLevel = "none" | "middle" | "ancestors";

type TruncatePathProps = {
  className?: string;
  path: string[];
  separator?: string;
};

const TruncatePath: FC<TruncatePathProps> = ({
  className,
  path,
  separator = " > ",
}) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const [currentTruncationLevel, setCurrentTruncationLevel] = useState<
    TruncationLevel | undefined
  >("none");
  const { width } = useMeasure(nameRef as MutableRefObject<HTMLElement>);

  const fullPath = path.join(separator);

  useEffect(() => {
    setCurrentTruncationLevel("none");
  }, [fullPath]);

  useEffect(() => {
    const element = nameRef.current;

    if (!element || !(element instanceof HTMLElement)) {
      return;
    }

    if (element.offsetWidth < element.scrollWidth) {
      setCurrentTruncationLevel((level) =>
        level === "none" ? "middle" : "ancestors"
      );
    }
  }, [width]);

  const first = path[0];
  const last = path[path.length - 1];

  const content =
    path.length <= 1 || currentTruncationLevel === "none"
      ? fullPath
      : path.length > 2 && currentTruncationLevel === "middle"
        ? `${first}${separator}${ellipsis}${separator}${last}`
        : `${ellipsis}${separator}${last}`;

  return (
    <Tooltip content={currentTruncationLevel === "none" ? null : fullPath}>
      <NoWrap as="div" ref={nameRef}>
        <span className={className}>{content}</span>
      </NoWrap>
    </Tooltip>
  );
};

export default TruncatePath;
